'use client';
import Image from 'next/image';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/locales/translations';


interface OfficeCard {
  flag: string;
  country: string;
  address: string;
  phone: string;
  website: string;
  email: string;
}

const contactTitles: Record<string, string> = {
  en: 'CONTACT US',
  fr: 'CONTACTEZ-NOUS',
  ar: 'اتصل بنا',
};

export default function ContactSection() {
  const { language } = useLanguage();
  const title = contactTitles[language] || contactTitles['en'];
  const t = translations[language];
  const offices = (translations[language as 'en' | 'fr' | 'ar']?.contact?.offices ?? []) as OfficeCard[]; 
  const flagMap: Record<string, string> = {
  '🇨🇦': '/assets/flags/canada.png',
  '🇫🇷': '/assets/flags/European union.png',
  '🇲🇷': '/assets/flags/mauritania.png',
  };
  return (
    <section id="contact" className="py-16 px-6 md:px-12 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Section Title */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#1a3a5c] tracking-wide">{title}</h2>
        </div>

        {/* Office Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {offices.map((office) => (
            <div
              key={office.country}
              className="bg-white rounded-2xl p-6 shadow-sm border border-blue-100 flex flex-col gap-4"
            >
              {/* Flag + Country */}
                <div className="flex flex-col items-center gap-2 pb-2 border-b border-blue-50">
                <div className="w-16 h-10 relative overflow-hidden rounded shadow-sm">
                  <Image
                  src={flagMap[office.flag] ?? ''}
                  alt={office.country}
                  fill
                  unoptimized
                  className="object-cover"
                  />
                </div>
                <h4 className="text-lg font-extrabold text-[#1a3a5c] tracking-widest">{office.country}</h4>
              </div>    

              {/* Contact Details */}
              <div className="flex flex-col gap-3 text-sm text-[#2d4a6b]">
                {/* Address */}
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0">
                    <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </span>
                  <span className="leading-snug">{office.address}</span>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-3">
                  <span className="flex-shrink-0">
                    <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                    </svg>
                  </span>
                  <a href={`https://wa.me/${office.phone.replace(/[\s+\-]/g, '')}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#1a3a5c] transition-colors">
                    {office.phone}
                  </a>
                </div>

                {/* Website */}
                <div className="flex items-center gap-3">
                  <span className="flex-shrink-0">
                    <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                    </svg>
                  </span>
                  <a href={`https://${office.website}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#1a3a5c] transition-colors">
                    {office.website}
                  </a>
                </div>

                {/* Email */}
                <div className="flex items-center gap-3">
                  <span className="flex-shrink-0">
                    <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                  </span>
                  <a href={`mailto:${office.email}`} className="hover:text-[#1a3a5c] transition-colors">
                    {office.email}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Bar */}
        <div className="bg-white/70 rounded-2xl px-6 py-5 border border-blue-100 mb-8">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-[#2d4a6b]">
            {/* Website */}
            <a href="https://www.autofitcanada.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-[#1a3a5c] transition-colors">
              <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
              www.autofitcanada.com
            </a>

            <span className="text-[#1a3a5c] font-bold select-none">—</span>

            {/* Phone */}
            <a href="https://wa.me/14506011665" className="flex items-center gap-1.5 hover:text-[#1a3a5c] transition-colors">
              <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
              +1 450 601 1665
            </a>

            <span className="text-[#1a3a5c] font-bold select-none">—</span>

            {/* Email */}
            <a href="mailto:info@autofitcanada.com" className="flex items-center gap-1.5 hover:text-[#1a3a5c] transition-colors">
              <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              info@autofitcanada.com
            </a>

            <span className="text-[#1a3a5c] font-bold select-none">—</span>

            {/* Facebook */}
            <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-[#1a3a5c] transition-colors">
              <svg className="w-4 h-4 text-[#1877f2]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span><span className="text-[#6b8aaa] text-xs mr-1">Facebook:</span>AUTOFIT CANADA LTD</span>
            </a>

            <span className="text-[#1a3a5c] font-bold select-none">—</span>

            {/* LinkedIn */}
            <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-[#1a3a5c] transition-colors">
              <svg className="w-4 h-4 text-[#0a66c2]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              <span><span className="text-[#6b8aaa] text-xs mr-1">LinkedIn:</span>AUTOFIT CANADA LTD</span>
            </a>
          </div>
        </div>

        {/* Closing Tagline */}
        <div className="text-center">
          <p className="text-base md:text-lg font-medium text-[#1a3a5c] italic">
            {language === 'fr' ? 'Votre passerelle vers les produits de la mer premium de Mauritanie' : 'Your Gateway to Premium Mauritanian Seafood'}
          </p>
        </div>
      </div>
    </section>
  );
}
